import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, Package, Truck, Mail, Phone, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';

const OrderConfirmation = () => {
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const savedOrder = localStorage.getItem('cartlixs-last-order');
    if (savedOrder) {
      setOrder(JSON.parse(savedOrder));
    } else {
      navigate('/');
    }
  }, [navigate]);

  if (!order) {
    return null;
  }

  const estimatedDelivery = new Date();
  estimatedDelivery.setDate(estimatedDelivery.getDate() + 5);
  const deliveryString = estimatedDelivery.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 py-12">
        {/* Success Header */}
        <div className="text-center mb-12">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="text-green-500" size={48} />
          </div>
          <h1 
            className="text-3xl md:text-4xl font-bold text-slate-800 mb-4"
            style={{ fontFamily: 'Fredoka, sans-serif' }}
          >
            Order Placed Successfully!
          </h1>
          <p className="text-slate-600 text-lg" style={{ fontFamily: 'Quicksand, sans-serif' }}>
            Thank you for shopping with Cartlixs. We've received your order and will process it soon.
          </p>
        </div>

        {/* Order Details Card */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
          {/* Order Number */}
          <div className="bg-sky-500 text-white p-6">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
              <div>
                <p className="text-sky-100 text-sm mb-1">Order Number</p>
                <p className="text-2xl font-bold" data-testid="order-number">{order.orderNumber}</p>
              </div>
              <div className="text-left md:text-right">
                <p className="text-sky-100 text-sm mb-1">Order Date</p>
                <p className="font-semibold">
                  {new Date(order.orderDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Order Items */}
          <div className="p-6 border-b">
            <h2 
              className="text-lg font-bold text-slate-800 mb-4"
              style={{ fontFamily: 'Nunito, sans-serif' }}
            >
              Order Items
            </h2>
            <div className="space-y-4">
              {order.items.map((item) => (
                <div key={item.id} className="flex gap-4">
                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-100">
                    <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-800">{item.name}</p>
                    <p className="text-sm text-slate-500">Qty: {item.quantity}</p>
                  </div>
                  <p className="font-bold text-slate-800">${(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Order Summary */}
          <div className="p-6 border-b bg-slate-50">
            <div className="space-y-2">
              <div className="flex justify-between text-slate-600">
                <span>Subtotal</span>
                <span>${order.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-slate-600">
                <span>Shipping</span>
                <span>{order.shipping === 0 ? 'FREE' : `$${order.shipping.toFixed(2)}`}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-slate-800 pt-2 border-t">
                <span>Total</span>
                <span data-testid="order-total">${order.total.toFixed(2)}</span>
              </div>
            </div>
          </div>

          {/* Shipping Info */}
          <div className="p-6 grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                <MapPin className="text-sky-500" size={18} />
                Shipping Address
              </h3>
              <p className="text-slate-600">
                {order.shippingAddress.firstName} {order.shippingAddress.lastName}<br />
                {order.shippingAddress.address}<br />
                {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}
              </p>
            </div>
            <div>
              <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
                <Truck className="text-sky-500" size={18} />
                Estimated Delivery
              </h3>
              <p className="text-slate-600">{deliveryString}</p>
              <p className="text-sm text-slate-500 mt-1">
                Payment Method: {order.paymentMethod === 'cod' ? 'Cash on Delivery' : 'Credit Card'}
              </p>
            </div>
          </div>
        </div>

        {/* Support Info */}
        <div className="bg-white rounded-2xl p-6 shadow-sm mb-8">
          <h3 
            className="font-bold text-slate-800 mb-4"
            style={{ fontFamily: 'Nunito, sans-serif' }}
          >
            Need Help?
          </h3>
          <p className="text-slate-600 mb-4">
            If you have any questions about your order, please contact our customer support team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href="mailto:support@cartlixs.store"
              className="flex items-center gap-2 text-sky-500 hover:text-sky-600"
            >
              <Mail size={18} />
              <span>support@cartlixs.store</span>
            </a>
            <a 
              href="tel:+1-888-435-6822"
              className="flex items-center gap-2 text-sky-500 hover:text-sky-600"
            >
              <Phone size={18} />
              <span>+1-888-435-6822</span>
            </a>
          </div>
        </div>

        {/* Continue Shopping */}
        <div className="text-center">
          <Link to="/">
            <Button 
              className="rounded-full px-8 py-6 bg-orange-400 hover:bg-orange-500 text-white font-bold text-lg shadow-[0px_4px_0px_0px_#cc8400]"
              data-testid="continue-shopping-btn"
            >
              Continue Shopping
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;
